import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  App,
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Form,
  InputNumber,
  Row,
  Skeleton,
  Switch,
  Typography,
} from "antd";
import {
  AiDetectionFilter,
  UpdateAiDetectionSettingsBody,
  useGetAiDetectionSettingsQuery,
  useUpdateAiDetectionSettingsMutation,
} from "api";
import { useAppSelector } from "store/hooks";
import { getRouteByName } from "libs";
import { getErrorMessage } from "components";

interface AiModerationFormValues {
  moderationEnabled: boolean;
  thresholds: Record<string, number>;
  enabled: Record<string, boolean>;
}

// Romanian section headers keyed by the backend `group`. The per-filter labels
// stay as the AI model's own (English) category keys — they are technical
// identifiers the model returns, so translating each would drift from the
// source of truth. Page chrome and help text are Romanian.
const GROUP_LABELS: Record<string, string> = {
  text: "Text (titlu si descriere)",
  racism: "Rasism si ura",
  violence: "Violenta",
  symbols: "Simboluri periculoase",
  sexual: "Continut sexual",
  drugs: "Droguri si substante",
  extremism: "Extremism si abuz",
};

const humanizeKey = (key: string): string =>
  key.charAt(0).toUpperCase() + key.slice(1);

const groupLabel = (group: string): string => GROUP_LABELS[group] ?? group;

export const AiModerationPage = () => {
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const [form] = Form.useForm<AiModerationFormValues>();

  const { isAll } = useAppSelector(
    (state) => state.loggedAdmin.parsedPermissions,
  );

  const { data, isLoading } = useGetAiDetectionSettingsQuery(undefined, {
    skip: !isAll,
  });

  const [updateSettings, { isLoading: isSaving, error, isSuccess }] =
    useUpdateAiDetectionSettingsMutation();

  useEffect(() => {
    if (!isAll) {
      navigate(getRouteByName("NotFoundPage"));
    }
  }, [isAll, navigate]);

  useEffect(() => {
    if (!data) return;
    const thresholds: Record<string, number> = {};
    const enabled: Record<string, boolean> = {};
    data.filters.forEach((filter) => {
      thresholds[filter.key] = filter.threshold;
      enabled[filter.key] = filter.enabled;
    });
    form.setFieldsValue({
      moderationEnabled: data.enabled,
      thresholds,
      enabled,
    });
  }, [data, form]);

  // Master toggle drives whether every per-filter control is editable, so the
  // page reads as inert at a glance when moderation is off. Watch the live form
  // value (not the cached data) so it reacts the instant the switch flips.
  const moderationEnabled = Form.useWatch("moderationEnabled", form);

  useEffect(() => {
    if (error) {
      notification.error({ message: getErrorMessage(error) });
    }
  }, [error, notification]);

  useEffect(() => {
    if (isSuccess) {
      notification.success({ message: "Setarile de moderare au fost salvate" });
    }
  }, [isSuccess, notification]);

  // Preserve the backend order (text filters first, then image groups) while
  // bucketing rows under their group card.
  const groups = useMemo<Array<[string, AiDetectionFilter[]]>>(() => {
    const map = new Map<string, AiDetectionFilter[]>();
    (data?.filters ?? []).forEach((filter) => {
      const bucket = map.get(filter.group) ?? [];
      bucket.push(filter);
      map.set(filter.group, bucket);
    });
    return Array.from(map.entries());
  }, [data]);

  const onFinish = (values: AiModerationFormValues) => {
    const filters: UpdateAiDetectionSettingsBody["filters"] = (
      data?.filters ?? []
    ).map((filter) => ({
      key: filter.key,
      threshold: values.thresholds?.[filter.key] ?? filter.threshold,
      enabled: values.enabled?.[filter.key] ?? filter.enabled,
    }));

    updateSettings({ enabled: values.moderationEnabled, filters });
  };

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  return (
    <Form<AiModerationFormValues>
      form={form}
      layout="vertical"
      onFinish={onFinish}
    >
      <Flex
        justify="space-between"
        align="center"
        style={{ marginBottom: 16 }}
        gap={16}
        wrap="wrap"
      >
        <Typography.Title level={3} style={{ margin: 0 }}>
          Moderare AI a continutului
        </Typography.Title>
        <Button type="primary" htmlType="submit" loading={isSaving}>
          Salveaza
        </Button>
      </Flex>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Cum functioneaza pragurile"
        description="Continutul unei postari este respins cand scorul returnat de AI pentru un filtru activ depaseste pragul setat. Prag mai mic = mai strict. Un filtru dezactivat este ignorat complet. Filtrele stricte aplicate botilor nu sunt afectate de aceasta pagina."
      />

      <Card style={{ marginBottom: 16 }}>
        <Form.Item
          name="moderationEnabled"
          label="Activeaza moderarea AI pe postari"
          valuePropName="checked"
          extra="Cand este oprita, postarile utilizatorilor nu mai sunt verificate AI la creare sau editare."
          style={{ marginBottom: 0 }}
        >
          <Switch />
        </Form.Item>
      </Card>

      <Row gutter={[16, 16]}>
        {groups.map(([group, filters]) => (
          <Col xs={24} lg={12} key={group}>
            <Card title={groupLabel(group)}>
              {filters.map((filter) => (
                <Flex
                  key={filter.key}
                  align="center"
                  justify="space-between"
                  gap={12}
                  style={{ marginBottom: 12 }}
                >
                  <Form.Item
                    name={["enabled", filter.key]}
                    valuePropName="checked"
                    noStyle
                  >
                    <Switch size="small" disabled={!moderationEnabled} />
                  </Form.Item>
                  <Typography.Text style={{ flex: 1 }}>
                    {humanizeKey(filter.key)}
                  </Typography.Text>
                  <Form.Item
                    name={["thresholds", filter.key]}
                    noStyle
                    rules={[
                      { required: true, message: "Prag" },
                      {
                        type: "number",
                        min: 0,
                        max: 100,
                        message: "0 - 100",
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      step={5}
                      precision={0}
                      addonAfter="%"
                      disabled={!moderationEnabled}
                      style={{ width: 120 }}
                    />
                  </Form.Item>
                </Flex>
              ))}
            </Card>
          </Col>
        ))}
      </Row>
    </Form>
  );
};
